import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type {
  CreateKPIAssignmentInput,
  CreateKPIInput,
  UpdateKPIAssignmentInput,
  UpdateKPIInput,
} from "../../domain/contracts";
import type {
  KPIOverviewExplainResult,
  KPIPeriodFilter,
} from "../../domain/types";
import {
  type KpiCalculationPeriodParams,
  kpiApi,
  kpiAssignmentApi,
  kpiCalculationApi,
} from "./client";

export const kpiKeys = {
  all: ["kpi"] as const,
  overview: (filter: KPIPeriodFilter) => ["kpi", "overview", filter] as const,
  overviewExplain: (
    filter: KPIPeriodFilter,
    args: {
      scope: "organization" | "department" | "position" | "employee";
      scopeDepartmentId?: string;
      scopePositionId?: string;
      kpiId?: string;
      includeSources?: boolean;
      includeRecordSamples?: boolean;
      recordSampleLimit?: number;
    },
  ) => ["kpi", "overview", "explain", filter, args] as const,
  list: (query: OffsetPageQueryDTO) => ["kpis", "list", query] as const,
  detail: (id: string) => ["kpis", "detail", id] as const,
  assignments: {
    all: ["kpi", "assignments"] as const,
    list: (
      query: OffsetPageQueryDTO & {
        kpiId?: string;
        assignmentType?: string;
        isActive?: boolean;
        departmentId?: string;
      },
    ) => ["kpi", "assignments", "list", query] as const,
    detail: (id: string) => ["kpi", "assignments", "detail", id] as const,
    impact: (kpiId: string) => ["kpi", "assignments", "impact", kpiId] as const,
  },
};

export function useKPIOverview(
  filter: KPIPeriodFilter,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: kpiKeys.overview(filter),
    queryFn: () => kpiApi.getOverview(filter),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

export type KPIOverviewExplainArgs = {
  scope: "organization" | "department" | "position" | "employee";
  scopeDepartmentId?: string;
  scopePositionId?: string;
  kpiId?: string;
  includeSources?: boolean;
  includeRecordSamples?: boolean;
  recordSampleLimit?: number;
};

export function useKPIOverviewExplainQuery(
  filter: KPIPeriodFilter,
  args: KPIOverviewExplainArgs | null,
  enabled: boolean,
) {
  return useQuery<KPIOverviewExplainResult>({
    queryKey: args
      ? kpiKeys.overviewExplain(filter, args)
      : ["kpi", "overview", "explain", "disabled"],
    queryFn: () => {
      if (!args) {
        throw new Error("overview explain: missing args");
      }
      return kpiApi.getOverviewExplain(filter, args);
    },
    enabled: enabled && !!args,
    staleTime: 1000 * 60 * 2,
  });
}

export function useKPIsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: kpiKeys.list(q),
    queryFn: () => kpiApi.list(q),
  });
}

export function useKPIQuery(id: string) {
  return useQuery({
    queryKey: kpiKeys.detail(id),
    queryFn: () => kpiApi.get(id),
    enabled: !!id,
  });
}

export function useCreateKPI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateKPIInput) => kpiApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
}

export function useUpdateKPI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateKPIInput }) =>
      kpiApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
      queryClient.invalidateQueries({ queryKey: kpiKeys.detail(variables.id) });
    },
  });
}

export function useDeleteKPI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => kpiApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
}

export function useCopyKPI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => kpiApi.copy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
}

export function useActivateKPI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => kpiApi.activate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
      queryClient.invalidateQueries({ queryKey: kpiKeys.detail(id) });
    },
  });
}

export function useDeactivateKPI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => kpiApi.deactivate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
      queryClient.invalidateQueries({ queryKey: kpiKeys.detail(id) });
    },
  });
}

// KPI Assignment hooks
export function useKPIAssignmentsQuery(
  query: Partial<
    OffsetPageQueryDTO & {
      kpiId?: string;
      assignmentType?: "employee" | "position" | "department";
      isActive?: boolean;
      departmentId?: string;
    }
  > = {},
) {
  const q: OffsetPageQueryDTO & {
    kpiId?: string;
    assignmentType?: "employee" | "position" | "department";
    isActive?: boolean;
    departmentId?: string;
  } = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    kpiId: query.kpiId,
    assignmentType: query.assignmentType,
    isActive: query.isActive,
    departmentId: query.departmentId,
  };
  return useQuery({
    queryKey: kpiKeys.assignments.list(q),
    queryFn: () => kpiAssignmentApi.list(q),
  });
}

export function useKPIAssignmentQuery(id: string) {
  return useQuery({
    queryKey: kpiKeys.assignments.detail(id),
    queryFn: () => kpiAssignmentApi.get(id),
    enabled: !!id,
  });
}

export function useCreateKPIAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateKPIAssignmentInput) =>
      kpiAssignmentApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
}

export function useUpdateKPIAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateKPIAssignmentInput;
    }) => kpiAssignmentApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.assignments.all });
      queryClient.invalidateQueries({
        queryKey: kpiKeys.assignments.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
}

export function useDeleteKPIAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => kpiAssignmentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
}

export function useKPIAssignmentImpactQuery(kpiId: string) {
  return useQuery({
    queryKey: kpiKeys.assignments.impact(kpiId),
    queryFn: () => kpiAssignmentApi.getImpact(kpiId),
    enabled: !!kpiId,
  });
}

// KPI calculation by period (preview + recalculate)
export const kpiCalculationKeys = {
  preview: (params: KpiCalculationPeriodParams) =>
    ["kpi", "calculation", "preview", params] as const,
};

export function useKpiCalculationPreview() {
  return useMutation({
    mutationFn: (params: KpiCalculationPeriodParams) =>
      kpiCalculationApi.getPreview(params),
  });
}

export function useKpiRecalculate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: KpiCalculationPeriodParams) =>
      kpiCalculationApi.recalculate(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.all });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
}
